import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { App, Input } from 'antd'
import type { SessionAccount } from '@super-mega/server/contracts/auth'
import { ORDER_SEARCH_BATCH_ASYNC_THRESHOLD, type RentalOrder } from '@super-mega/server/contracts/order'
import { refreshMoneyQueries } from '@/features/bill/index.ts'
import { useBatchJobCompletion } from '@/hooks/use-batch-job-completion.ts'
import type { PageTableNavigate } from '@/hooks/use-page-table.ts'
import { useTRPC } from '@/trpc/react.ts'
import { useOrderBatchUpdateRefresh } from '../hooks/use-order-batch-update-refresh.ts'
import { type RentalListSearch } from '../rental-list-search.ts'

export function useRentalListActions({
  search,
  navigateSearch,
  currentSession,
  rows,
  total
}: {
  search: RentalListSearch
  navigateSearch: PageTableNavigate<RentalListSearch>
  currentSession: SessionAccount
  rows: RentalOrder[] | undefined
  total: number
}) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message, modal } = App.useApp()
  const router = useRouter()
  const remove = useMutation(trpc.order.rental.remove.mutationOptions())

  const batchRemove = useMutation(trpc.order.rental.batchRemove.mutationOptions())

  const batchBySearch = useMutation(trpc.order.rental.batchBySearch.mutationOptions())

  const [searchBatchWarnings, setSearchBatchWarnings] = useState<string[]>([])

  const [searchBatchJobId, setSearchBatchJobId] = useState<string>()

  const searchBatchStatus = useQuery({
    ...trpc.order.rental.batchBySearchStatus.queryOptions({ jobId: searchBatchJobId ?? '' }),
    enabled: Boolean(searchBatchJobId),
    refetchInterval: (query) =>
      ['completed', 'failed', 'missing'].includes(query.state.data?.state ?? '') ? false : 3000
  })

  const archive = useMutation(trpc.order.rental.archive.mutationOptions())

  const busy = remove.isPending || batchRemove.isPending || batchBySearch.isPending || archive.isPending

  const [selection, setSelection] = useState<{ search: RentalListSearch; ids: number[] }>({ search, ids: [] })

  const pageIds = new Set(rows?.map((order) => order.id))

  const selectedIds = selection.search === search ? selection.ids.filter((id) => pageIds.has(id)) : []

  const invalidateRentalState = async () => {
    await refreshMoneyQueries(cache, trpc)
  }

  const refreshAfterRemoval = async () => {
    setSelection({ search, ids: [] })
    await Promise.all([
      refreshMoneyQueries(cache, trpc),
      cache.invalidateQueries(trpc.order.groups.list.queryFilter()),
      cache.invalidateQueries(trpc.order.groups.overview.queryFilter()),
      cache.invalidateQueries(trpc.file.list.queryFilter()),
      cache.invalidateQueries(trpc.order.rental.currentContract.queryFilter()),
      cache.invalidateQueries(trpc.contract.document.list.queryFilter())
    ])
    const refreshed = cache.getQueryData(trpc.order.rental.list.queryKey(search))
    if (search.current > 1 && refreshed && refreshed.data.length === 0) {
      const current = Math.max(1, Math.ceil(refreshed.total / search.pageSize))
      await navigateSearch({ search: (previous) => ({ ...previous, current }) })
    }
  }

  const refreshBatchUpdate = useOrderBatchUpdateRefresh(refreshAfterRemoval)

  useBatchJobCompletion({
    jobId: searchBatchJobId,
    state: searchBatchStatus.data?.state,
    onComplete: refreshBatchUpdate,
    onError: () => {
      void message.warning('列表刷新失败，请手动刷新查看结果')
    }
  })

  const confirmBatchRemove = () => {
    const ids = [...selectedIds]
    modal.confirm({
      title: `删除已选 ${ids.length} 个租赁订单？`,
      content: currentSession.isSuperAdmin
        ? '系统会逐条删除订单、关联账单及明细，并清除占用记录。已有资金分配或关联合同的订单计为失败，其余继续删除。不会删除资金流水、收据或企业档案，删除后不可恢复。'
        : '系统会逐条尝试删除，仅可删除待处理或已归档且没有账单、合同的订单。不符合条件的订单计为失败，其余继续删除，删除后不可恢复。',
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const result = await batchRemove.mutateAsync({ ids })
          const text = `删除完成：成功 ${result.successCount} 个，失败 ${result.failedCount} 个`
          if (result.failedCount === 0) {
            void message.success(text)
          } else if (result.successCount === 0) {
            void message.error(text)
          } else {
            void message.warning(text)
          }
          await refreshAfterRemoval()
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '批量删除租赁订单失败')
          throw error
        }
      }
    })
  }

  const confirmSearchBatch = (action: 'delete' | 'forceDelete') => {
    const count = total
    const expected = `强制删除 ${count} 个订单`
    let confirmation = ''
    const execute = async () => {
      try {
        const result = await batchBySearch.mutateAsync({
          action,
          search,
          expectedCount: count,
          confirmation: action === 'forceDelete' ? confirmation : undefined
        })
        setSearchBatchWarnings(result.warnings)
        if (result.queued) {
          setSearchBatchJobId(result.jobId)
          void message.success(`已提交 ${result.count} 个订单到异步队列`)
        } else {
          const summary = `处理完成：成功 ${result.successCount} 个，失败 ${result.failedCount} 个`
          if (result.failedCount || result.warnings.length) {
            void message.warning(summary)
          } else {
            void message.success(summary)
          }
        }
        await refreshAfterRemoval()
      } catch (error) {
        void message.error(error instanceof Error ? error.message : '按搜索批量操作失败')
        throw error
      }
    }
    modal.confirm({
      title: `${action === 'forceDelete' ? '强制删除' : '删除'}当前搜索结果中的 ${count} 个租赁订单？`,
      content:
        action === 'forceDelete' ? (
          <div>
            <p>将永久删除匹配的订单及关联数据。</p>
            <p>请输入「{expected}」确认</p>
            <Input
              aria-label="确认按搜索强制删除"
              onChange={(event) => {
                confirmation = event.target.value
              }}
            />
          </div>
        ) : (
          `将按当前已提交的搜索条件处理全部匹配订单，逐条执行并统计失败；超过 ${ORDER_SEARCH_BATCH_ASYNC_THRESHOLD} 条会进入异步队列。删除后不可恢复。`
        ),
      okText: '确认执行',
      okButtonProps: { danger: true },
      onOk: () => {
        if (action === 'forceDelete' && confirmation.trim() !== expected) {
          void message.error(`请输入「${expected}」`)
          throw new Error('确认文字不匹配')
        }
        if (action === 'forceDelete') {
          modal.confirm({
            title: `第二次确认：永久删除 ${count} 个租赁订单？`,
            content: '订单及关联数据将永久删除，无法恢复。',
            okText: '永久删除',
            okButtonProps: { danger: true },
            onOk: execute
          })
          return
        }
        return execute()
      }
    })
  }

  const openWorkspaceBySearch = async (kind: 'contracts' | 'materials') => {
    if (kind === 'materials' && search.subjectAssociation !== 'unlinked') {
      void message.warning('请先将搜索条件「服务对象关联」设为「未关联」并查询，再批量补齐资料')
      return
    }
    if (!total) {
      void message.warning('当前搜索条件下没有订单')
      return
    }
    if (total > 100) {
      void message.warning('单次最多处理 100 个租赁订单，请缩小搜索范围后重试')
      return
    }
    // 在点击事件内先打开标签页，避免异步查询完成后被浏览器拦截。
    const workspaceTab = window.open('', '_blank')
    if (!workspaceTab) {
      void message.warning('浏览器阻止了新标签页，请允许弹出窗口后重试')
      return
    }
    workspaceTab.opener = null
    try {
      const options =
        kind === 'materials'
          ? trpc.order.rental.materialsBySearch.queryOptions(search)
          : trpc.order.rental.contractBySearch.queryOptions(search)
      const result = await cache.fetchQuery({ ...options, staleTime: 0 })
      if (!workspaceTab.closed) {
        workspaceTab.location.replace(
          router.buildLocation({
            to: kind === 'materials' ? '/orders/rentals/materials/batch' : '/orders/rentals/contracts/batch',
            search: { ids: result.ids.join(',') }
          }).href
        )
      }
    } catch (error) {
      workspaceTab.close()
      void message.error(error instanceof Error ? error.message : '打开批量操作失败')
    }
  }

  const confirmRemove = (order: RentalOrder) => {
    modal.confirm({
      title: `确认删除租赁订单 ${order.orderNo}？`,
      content: currentSession.isSuperAdmin
        ? '将删除订单、关联账单及明细，并清除租赁占用记录，不可恢复。已有资金分配或关联合同时需先处理；不会删除资金流水、收据、企业、法人档案或证件文件。'
        : '仅可删除待处理或已归档且没有账单、合同的订单，删除后不可恢复。企业、法人档案及已上传的证件文件会保留。',
      okText: '删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await remove.mutateAsync({ rentalOrderId: order.id })
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '租赁订单删除失败')
          throw error
        }
        void message.success('租赁订单已删除')
        await refreshAfterRemoval()
      }
    })
  }
  return {
    selectedIds,
    setSelection,
    batchRemove,
    batchBySearch,
    busy,
    searchBatchWarnings,
    searchBatchJobId,
    setSearchBatchJobId,
    searchBatchStatus,
    refreshBatchUpdate,
    confirmBatchRemove,
    confirmSearchBatch,
    confirmRemove,
    archive,
    invalidateRentalState,
    refreshAfterRemoval,
    openWorkspaceBySearch
  }
}
